import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './queryClient';
import { ThemedToaster } from './components/ThemedToaster';
import * as Sentry from '@sentry/react';
import {
  isSupportedWebBrowser,
  resolveSentryEnvironment,
  resolveSentryTracesSampleRate,
  supportsSessionReplay,
} from '@court-sync/shared';
import { isWebSentryEnabled } from './lib/sentry';

import App from './App';
import { AuthProvider } from './store/auth';
import { ThemeProvider } from './store/theme';
import { GoogleTagManager } from './components/GoogleTagManager';

import 'react-datepicker/dist/react-datepicker.css';
import './index.css';

const webBrowserSupported = isSupportedWebBrowser();

const sentryEnabled = webBrowserSupported && isWebSentryEnabled();

// Initialize Sentry
if (sentryEnabled) {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE?.trim() || undefined;
  const sentryEnvironment = resolveSentryEnvironment(
    import.meta.env.VITE_SENTRY_ENVIRONMENT,
    import.meta.env.MODE
  );
  const sessionSampleRate = resolveSentryTracesSampleRate(sentryEnvironment);
  const sessionReplayEnabled = supportsSessionReplay();

  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    ...(sentryRelease ? { release: sentryRelease } : {}),
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(sessionReplayEnabled
        ? [
            Sentry.replayIntegration({
              maskAllText: false,
              blockAllMedia: false,
              // Avoid cross-origin iframe access errors (Stripe Connect, Firebase Auth, GTM).
              block: [
                'iframe[src*="stripe.com"]',
                'iframe[src*="firebaseapp.com"]',
                'iframe[src*="googletagmanager.com"]',
              ],
            }),
          ]
        : []),
      Sentry.consoleLoggingIntegration({ levels: ['error', 'warn'] }),
    ],
    enableLogs: true,
    tracesSampleRate: sessionSampleRate,
    replaysSessionSampleRate: sessionReplayEnabled ? sessionSampleRate : 0,
    replaysOnErrorSampleRate: sessionReplayEnabled ? 1.0 : 0,
  });
}

if (webBrowserSupported) {
  ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <GoogleTagManager />
        <ThemeProvider>
          <AuthProvider>
            {sentryEnabled ? (
            <Sentry.ErrorBoundary fallback={({ error }) => (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <h1>Something went wrong</h1>
                <p>{error instanceof Error ? error.message : 'An unexpected error occurred'}</p>
                <button onClick={() => window.location.reload()}>Reload</button>
              </div>
            )}>
              <App />
            </Sentry.ErrorBoundary>
            ) : (
              <App />
            )}
            <ThemedToaster />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
      {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  </React.StrictMode>
  );
}